import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { WHATSAPP_URL, CONTACT_EMAIL } from "@/data/translations";

const FAQ_SCHEMA_ID = "faq-page-jsonld";

const FAQ = () => {
  const { t, lang, setLang } = useLang();
  useDocumentMeta(lang);
  const [openIdx, setOpenIdx] = useState(0);

  /* Override title/description for /faq specifically */
  useEffect(() => {
    const titles = {
      fr: "FAQ — Entrée Express Francophone · Marocanada",
      en: "FAQ — Francophone Express Entry · Marocanada",
    };
    const descs = {
      fr: "Réponses aux questions les plus posées sur l'Entrée Express Francophone : niveau de français requis, seuil CRS, délais, coûts, documents marocains. Par Marocanada, consultants RCIC agréés.",
      en: "Answers to the most-asked questions about Francophone Express Entry: required French level, CRS threshold, timelines, costs, Moroccan documents. By Marocanada, licensed RCIC consultants.",
    };
    document.title = titles[lang] || titles.fr;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", descs[lang] || descs.fr);
  }, [lang]);

  /* Inject FAQPage JSON-LD scoped to /faq */
  useEffect(() => {
    const items = t.faq.items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    }));
    const payload = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": "https://marocanada.org/faq#faqpage",
      mainEntityOfPage: "https://marocanada.org/faq",
      inLanguage: lang,
      mainEntity: items,
    };
    let el = document.getElementById(FAQ_SCHEMA_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = FAQ_SCHEMA_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => {
      const e = document.getElementById(FAQ_SCHEMA_ID);
      if (e) e.remove();
    };
  }, [t, lang]);

  return (
    <main
      data-testid="faq-page"
      className="bg-[#0D1117] text-[#F5F2EE] min-h-screen"
    >
      {/* Mini-header */}
      <header className="relative border-b border-[#F5F2EE]/8">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="faq-logo"
            dir="ltr"
            className="flex items-baseline gap-0 select-none font-display"
          >
            <span className="text-2xl text-[#C8A55E] font-normal tracking-tight">
              Maro
            </span>
            <span className="text-2xl italic text-[#F5F2EE] font-normal tracking-tight">
              canada
            </span>
          </Link>

          <div className="flex items-center gap-5">
            <div className="hidden sm:flex items-center gap-3 text-xs">
              {["fr", "en"].map((l, i) => (
                <div key={l} className="flex items-center gap-3">
                  <button
                    data-testid={`faq-lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`font-mono-accent text-[10px] transition-colors ${
                      lang === l
                        ? "text-[#E8A045]"
                        : "text-[#F5F2EE]/40 hover:text-[#F5F2EE]/70"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                  {i === 0 && <span className="block w-px h-3 bg-[#F5F2EE]/15" />}
                </div>
              ))}
            </div>
            <Link
              to="/"
              data-testid="faq-back-home"
              className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors"
            >
              {t.faq.back}
            </Link>
          </div>
        </div>
      </header>

      {/* Title */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-20 sm:pt-28 pb-12">
        <span className="brushstroke mb-6" />
        <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
          {t.faq.eyebrow}
        </div>
        <h1
          data-testid="faq-headline"
          className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] text-[#F5F2EE] leading-[1.05] tracking-tight max-w-4xl"
        >
          {t.faq.headline}
        </h1>
      </section>

      {/* Accordion */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pb-20">
        <div className="divide-y divide-[#F5F2EE]/10 border-t border-[#F5F2EE]/10">
          {t.faq.items.map((item, i) => {
            const open = openIdx === i;
            return (
              <article key={i} data-testid={`faq-item-${i}`} className="py-6">
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start justify-between gap-6 text-left group"
                  aria-expanded={open}
                >
                  <div className="flex items-start gap-5 flex-1">
                    <span className="font-mono-accent text-[10px] text-[#E8A045] pt-2 w-8 flex-shrink-0">
                      0{i + 1}
                    </span>
                    <h2 className="font-display text-xl sm:text-2xl lg:text-[1.75rem] text-[#F5F2EE] leading-tight tracking-tight group-hover:text-[#E8A045] transition-colors">
                      {item.q}
                    </h2>
                  </div>
                  <span className="flex-shrink-0 w-10 h-10 rounded-full border border-[#F5F2EE]/15 flex items-center justify-center text-[#E8A045] group-hover:border-[#E8A045] transition-colors mt-1">
                    {open ? <Minus className="w-4 h-4" strokeWidth={1.5} /> : <Plus className="w-4 h-4" strokeWidth={1.5} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pt-5 pl-[3.25rem] pr-12">
                        <p className="font-body text-[15px] sm:text-base text-[#F5F2EE]/65 leading-relaxed max-w-3xl">
                          {item.a}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1C1C1E] border-t border-[#F5F2EE]/8">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-3xl sm:text-4xl text-[#F5F2EE] leading-tight tracking-tight">
              {t.faq.cta.title}
            </h3>
            <p className="mt-4 font-body text-[#F5F2EE]/60 max-w-xl">
              {t.faq.cta.sub}
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <Link
              to="/#contact"
              data-testid="faq-cta-book"
              className="inner-border-btn group inline-flex items-center justify-center gap-3 bg-[#2FBFAD] hover:bg-[#239B8C] text-[#0D1117] px-7 py-3.5 rounded-full font-mono-accent text-[11px] font-medium whitespace-nowrap"
            >
              <span>{t.faq.cta.button}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </Link>
            <div className="flex items-center gap-4 text-xs">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="faq-whatsapp" className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors">
                WhatsApp
              </a>
              <span className="w-px h-3 bg-[#F5F2EE]/15" />
              <a href={`mailto:${CONTACT_EMAIL}`} data-testid="faq-email" className="font-mono-accent text-[10px] text-[#F5F2EE]/65 hover:text-[#E8A045] transition-colors">
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQ;
